/* eslint-disable @typescript-eslint/camelcase */
import { Grid, Typography, makeStyles } from '@material-ui/core';
import React, { memo } from 'react';
import Breadcrumbs, { BreadcrumbsFields } from 'components/Breadcrumbs';
import WarningOutlinedIcon from '@material-ui/icons/WarningOutlined';
import Permission from 'components/ValidPermission';
import i18n from 'i18n';
import { useHistory, useParams } from 'react-router';
import Button from 'components/Button';
import { connect } from 'react-redux';
import { modalOpen, modalClose } from 'modules/modal/actions';
import ApplicationState from 'store/interfaces';
import {
  handleRemoveClientRequest,
  getClientByIdRequest,
} from 'modules/clients/actions';
import {
  CompanyData,
  PartnerData,
  PermissionsData,
} from 'modules/authenticate/interfaces';
import { ModalFields } from 'modules/modal/interfaces';
import { ClientFields } from 'modules/clients/interfaces';
import { selectCompany, selectPartner } from 'modules/authenticate/actions';
import PageTitle from '../../../../components/PageTitle';
import NavBarClients from '../../NavBarClients';
import styles from './styles';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 25,
  search: '',
};

interface TemplateProps {
  clientSelected: ClientFields | null;
  current:
    | 'configurations'
    | 'users'
    | 'applications'
    | 'sessions'
    | 'access-restrictions'
    | 'maintenance'
    | 'audit'
    | 'security'
    | 'digital-certificates';
  title?: string;
  breadcrumbs?: BreadcrumbsFields[];
  subtitle?: string;
  children?: React.ReactNode;
  button?: React.ReactNode;
  headerComponent?: React.ReactNode;
  companySelected: CompanyData | null;
  partnerSelected: PartnerData | null;
  getClientByIdRequest: ({ id }) => void;
  permissions?: PermissionsData;
  handleRemoveClientRequest: ({ clientUuid, query }) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  selectCompany: (company: CompanyData | null) => void;
  selectPartner: (partner: PartnerData | null) => void;
}

const ClientsTemplate = ({
  clientSelected,
  current,
  subtitle,
  title,
  children,
  button,
  headerComponent,
  breadcrumbs,
  companySelected,
  partnerSelected,
  permissions,
  getClientByIdRequest,
  handleRemoveClientRequest,
  modalOpen,
  modalClose,
  selectCompany,
  selectPartner,
}: TemplateProps) => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const requiredRefresh = ['configurations', 'maintenance'].includes(current);

  React.useEffect(() => {
    if (clientSelected?.client_uuid !== id || requiredRefresh) {
      getClientByIdRequest({ id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (
      clientSelected?.company &&
      clientSelected?.partner &&
      clientSelected?.partner?.partner_uuid !== partnerSelected?.id
    ) {
      selectCompany({
        id: clientSelected?.company?.uuid,
        name: clientSelected?.company?.name,
        shortName: clientSelected?.company?.name,
      });
      selectPartner({
        id: clientSelected?.partner?.partner_uuid,
        name: clientSelected?.partner?.name,
        shortName: clientSelected?.partner?.name,
      });
    }
  }, [clientSelected, partnerSelected, selectCompany, selectPartner]);

  const handleRemoveClient = () => {
    modalOpen({
      title: i18n.t('CLIENTS_PAGE.WANT_REMOVE_CLIENT_KEY'),
      content: i18n.t('CLIENTS_PAGE.REMOVE_CLIENT_WARNING_MSG_KEY'),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              handleRemoveClientRequest({
                clientUuid: clientSelected?.client_uuid,
                query: {
                  ...initialQuery,
                  company_uuid: permissions?.can_view_all_resources
                    ? ''
                    : companySelected?.id,
                  partner_uuid: permissions?.can_view_all_resources
                    ? ''
                    : partnerSelected?.id,
                },
              });
              history.push('/clients');
              modalClose();
            }}
          >
            {i18n.t('REMOVE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  return (
    <Grid container>
      <Grid container justify="space-between">
        <Grid container justify="flex-start" className={classes.clientHead}>
          <PageTitle
            title={
              clientSelected?.client_uuid !== id
                ? `${i18n.t('LOADING_KEY')}...`
                : clientSelected?.name
            }
            subtitle={breadcrumbs ? <Breadcrumbs values={breadcrumbs} /> : ''}
          />
          {clientSelected?.in_maintenance && (
            <Grid item className={classes.maintenanceWarn}>
              <WarningOutlinedIcon
                color="error"
                className={classes.maintenanceIcon}
              />
              <Typography className={classes.maintenanceText}>
                {i18n.t('CLIENT_MAINTENANCE_WARNING_KEY')}
              </Typography>
            </Grid>
          )}
        </Grid>
        <Permission value="delete_client">
          <Button
            deleteButton
            id="delete-button"
            onClick={handleRemoveClient}
            className={classes.red}
          >
            {i18n.t('REMOVE_KEY')}
          </Button>
        </Permission>
      </Grid>
      <NavBarClients current={current} />
      <Grid className={classes.sectionTitle}>
        {!!headerComponent && headerComponent}
        <Grid className={classes.headerContent} container>
          <PageTitle title={title} subtitle={subtitle} />
          {!!button && button}
        </Grid>
        {children}
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data, partnerSelected, companySelected },
  clients: { clientSelected },
}: ApplicationState) => ({
  clientSelected,
  companySelected,
  partnerSelected,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  getClientByIdRequest,
  modalOpen,
  modalClose,
  handleRemoveClientRequest,
  selectCompany,
  selectPartner,
})(memo(ClientsTemplate));
